import { Component, Inject, ElementRef, ViewChild, OnInit } from '@angular/core';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { ChatService } from '../../../services/chat.service';
import { AuthService } from '../../../services/auth.service';

export interface MessageDialogData {
  candidatId?: string | number;
  candidatName?: string;
  missionId?: string | number;
  missionTitle?: string;
}

@Component({
  selector: 'app-message-dialog',
  templateUrl: './message-dialog.component.html',
  styleUrls: ['./message-dialog.component.scss']
})
export class MessageDialogComponent implements OnInit {
  @ViewChild('fileInput') fileInput!: ElementRef;

  messageText = '';
  selectedFile: File | null = null;
  isRecording = false;
  isSending = false;
  showSuccess = false;
  showError = false;
  errorMessage = '';
  maxLength = 500;
  recordingDuration = 0;
  recordingInterval: any;
  mediaRecorder: MediaRecorder | null = null;
  audioChunks: Blob[] = [];

  constructor(
    public dialogRef: MatDialogRef<MessageDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public data: MessageDialogData,
    private chatService: ChatService,
    private authService: AuthService
  ) {
    if (!data) {
      console.warn('MessageDialog: No data provided to dialog');
    }
  }

  ngOnInit(): void {
    if (!this.data || !this.data.candidatId) {
      console.warn('MessageDialog: Missing required data');
      return;
    }

    const user = this.authService.user;
    if (user && user.id) {
      // Connect to chat with candidatId and agency context
      const candidatId = parseInt(this.data.candidatId.toString(), 10);
      this.chatService.connect(candidatId, user.id);
    }
  }

  get remainingChars(): number {
    return this.maxLength - this.messageText.length;
  }

  onFileSelected(event: any): void {
    if (event.target.files && event.target.files.length > 0) {
      const file = event.target.files[0];
      const validTypes = ['image/png', 'image/jpeg', 'image/jpg', 'application/pdf', 'video/mp4', 'video/webm', 'audio/ogg'];
      const maxSizeInBytes = 5 * 1024 * 1024; // 5MB

      if (!validTypes.includes(file.type)) {
        this.showErrorMessage('Seuls les fichiers PNG, JPEG, JPG, PDF et vidéos sont autorisés');
        return;
      }

      if (file.size > maxSizeInBytes) {
        this.showErrorMessage('La taille du fichier ne doit pas dépasser 5 Mo');
        return;
      }

      this.selectedFile = file;
      this.sendFileAttachment();
    }
  }

  triggerFileInput(): void {
    this.fileInput.nativeElement.click();
  }

  sendFileAttachment(): void {
    if (!this.selectedFile) return;

    const user = this.authService.user;
    if (!user) {
      this.showErrorMessage('Erreur : Utilisateur non connecté');
      return;
    }

    if (!this.data?.candidatId) {
      this.showErrorMessage('Erreur : Candidat non disponible');
      return;
    }

    this.isSending = true;
    this.showError = false;
    this.showSuccess = false;

    // Capture data before async operation
    const candidatId = parseInt(this.data!.candidatId.toString(), 10);
    const missionId = this.data!.missionId ? parseInt(this.data!.missionId.toString(), 10) : undefined;

    const reader = new FileReader();
    reader.onload = (event: any) => {
      const base64String = event.target.result.split(',')[1];

      let attachmentType: 'IMAGE' | 'PDF' | 'VIDEO' = 'IMAGE';
      if (this.selectedFile!.type === 'application/pdf') {
        attachmentType = 'PDF';
      } else if (this.selectedFile!.type.startsWith('video/')) {
        attachmentType = 'VIDEO';
      }

      // Format missionRef with better formatting
      let missionRef = '';
      if (this.data!.missionTitle && this.data!.missionId) {
        missionRef = `Mission ref : ${this.data!.missionTitle}`;
      } else if (this.data!.missionTitle) {
        missionRef = this.data!.missionTitle;
      }

      const message: any = {
        senderId: user.id,
        senderType: 'AGENCY',
        senderName: 'Agence',
        candidatId: candidatId,
        agencyId: user.id,
        message: '',
        timestamp: new Date(),
        isRead: false,
        attachmentType: attachmentType,
        attachmentPath: base64String,
        attachmentFileName: this.selectedFile!.name,
        missionId: missionId,
        missionRef: missionRef
      };

      this.chatService.sendMessage(message);
      this.selectedFile = null;

      this.showSuccess = true;
      setTimeout(() => {
        this.dialogRef.close({ success: true });
      }, 1500);
    };

    reader.onerror = () => {
      this.showErrorMessage('Erreur lors de la lecture du fichier');
      this.isSending = false;
    };

    reader.readAsDataURL(this.selectedFile);
  }

  startRecording(): void {
    if (this.isRecording) {
      this.stopRecording();
      return;
    }

    navigator.mediaDevices.getUserMedia({ audio: true })
      .then((stream: MediaStream) => {
        this.mediaRecorder = new MediaRecorder(stream);
        this.audioChunks = [];
        this.recordingDuration = 0;

        this.mediaRecorder.ondataavailable = (event: BlobEvent) => {
          this.audioChunks.push(event.data);
        };

        this.mediaRecorder.onstop = () => {
          const audioBlob = new Blob(this.audioChunks, { type: 'audio/webm' });
          this.sendVoiceMessage(audioBlob);
          stream.getTracks().forEach(track => track.stop());
          clearInterval(this.recordingInterval);
        };

        this.mediaRecorder.start();
        this.isRecording = true;

        // Start duration counter
        this.recordingInterval = setInterval(() => {
          this.recordingDuration++;
        }, 1000);
      })
      .catch((error: any) => {
        this.showErrorMessage('Impossible d\'accéder au microphone');
        console.error('Error accessing microphone:', error);
      });
  }

  stopRecording(): void {
    if (this.mediaRecorder && this.isRecording) {
      this.mediaRecorder.stop();
      this.isRecording = false;
    }
  }

  sendVoiceMessage(audioBlob: Blob): void {
    const user = this.authService.user;
    if (!user) {
      this.showErrorMessage('Erreur : Utilisateur non connecté');
      return;
    }

    if (!this.data?.candidatId) {
      this.showErrorMessage('Erreur : Candidat non disponible');
      return;
    }

    this.isSending = true;
    this.showError = false;
    this.showSuccess = false;

    // Capture data before async operation
    const candidatId = parseInt(this.data!.candidatId.toString(), 10);
    const missionId = this.data!.missionId ? parseInt(this.data!.missionId.toString(), 10) : undefined;

    const reader = new FileReader();
    reader.onload = (event: any) => {
      const base64String = event.target.result.split(',')[1];

      // Format missionRef with better formatting
      let missionRef = '';
      if (this.data!.missionTitle && this.data!.missionId) {
        missionRef = `Mission ref : ${this.data!.missionTitle}`;
      } else if (this.data!.missionTitle) {
        missionRef = this.data!.missionTitle;
      }

      const message: any = {
        senderId: user.id,
        senderType: 'AGENCY',
        senderName: 'Agence',
        candidatId: candidatId,
        agencyId: user.id,
        message: '',
        timestamp: new Date(),
        isRead: false,
        attachmentType: 'VOICE',
        attachmentPath: base64String,
        attachmentFileName: `voice_${Date.now()}.webm`,
        missionId: missionId,
        missionRef: missionRef
      };

      this.chatService.sendMessage(message);

      this.showSuccess = true;
      setTimeout(() => {
        this.dialogRef.close({ success: true });
      }, 1500);
    };

    reader.onerror = () => {
      this.showErrorMessage('Erreur lors de l\'envoi du message vocal');
      this.isSending = false;
    };

    reader.readAsDataURL(audioBlob);
  }

  sendMessage(): void {
    if (!this.messageText.trim() || this.isSending) {
      return;
    }

    const user = this.authService.user;
    if (!user) {
      this.showErrorMessage('Erreur : Utilisateur non connecté');
      return;
    }

    if (!this.data?.candidatId) {
      this.showErrorMessage('Erreur : Candidat non disponible');
      return;
    }

    this.isSending = true;
    this.showError = false;
    this.showSuccess = false;

    try {
      // Format missionRef with better formatting
      let missionRef = '';
      if (this.data.missionTitle && this.data.missionId) {
        missionRef = `Mission ref : ${this.data.missionTitle}`;
      } else if (this.data.missionTitle) {
        missionRef = this.data.missionTitle;
      }

      const message: any = {
        senderId: user.id,
        senderType: 'AGENCY',
        senderName: 'Agence',
        candidatId: parseInt(this.data.candidatId.toString(), 10),
        agencyId: user.id,
        message: this.messageText.trim(),
        timestamp: new Date(),
        isRead: false,
        missionId: this.data.missionId ? parseInt(this.data.missionId.toString(), 10) : undefined,
        missionRef: missionRef
      };

      this.chatService.sendMessage(message);

      this.showSuccess = true;
      this.messageText = '';

      // Close dialog after a short delay
      setTimeout(() => {
        this.dialogRef.close({ success: true });
      }, 1500);
    } catch (error) {
      this.showErrorMessage('Erreur lors de l\'envoi du message');
      this.isSending = false;
    }
  }

  showErrorMessage(message: string): void {
    this.errorMessage = message;
    this.showError = true;
    setTimeout(() => {
      this.showError = false;
    }, 3000);
  }

  close(): void {
    if (this.isRecording) {
      this.stopRecording();
    }
    this.dialogRef.close();
  }
} 